import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Type,
  Heading2,
  Image as ImageIcon,
  Video as VideoIcon,
  Code as EmbedIcon,
  FileText,
  PenTool,
  Plus,
  Trash2,
  GripVertical,
  Loader2,
} from 'lucide-react';
import type { ContentBlockType } from '@eduborg/shared';
import type { ContentBlockShape } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  useBlocksQuery,
  useCreateBlockMutation,
  useDeleteBlockMutation,
  useUpdateBlockMutation,
} from '@/hooks/queries/useCourseDetail';
import { defaultPayloadFor, parsePayload, type BlockPayload } from './types';
import { ConvertLessonMenu } from './ConvertLessonMenu';
import { SparkLessonButton } from './SparkLessonButton';

/**
 * The lesson editor — an ordered list of content_blocks rendered with type-
 * specific editors. The 8 supported types live in `./types.ts`; here we wire
 * fetch + create + update + delete to the api Worker via TanStack Query.
 *
 * R2-dependent blocks (image / video / file / freehand asset upload) are
 * stubbed with a "uploads enable in Phase 0 once R2 is online" placeholder so
 * they're authorable now and just light up later.
 */
export function BlockEditor({
  token,
  itemId,
  readOnly,
}: {
  token: string;
  itemId: string;
  readOnly: boolean;
}) {
  const blocksQuery = useBlocksQuery(token, itemId);
  const createBlock = useCreateBlockMutation(token, itemId);
  const updateBlock = useUpdateBlockMutation(token, itemId);
  const deleteBlock = useDeleteBlockMutation(token, itemId);

  const blocks = blocksQuery.data?.blocks ?? [];

  const onAdd = useCallback(
    (type: ContentBlockType) => {
      const payload = defaultPayloadFor(type);
      createBlock.mutate({ type, payloadJson: JSON.stringify(payload) });
    },
    [createBlock],
  );

  const onUpdate = useCallback(
    (block: ContentBlockShape, payload: BlockPayload) => {
      updateBlock.mutate({ blockId: block.id, payloadJson: JSON.stringify(payload) });
    },
    [updateBlock],
  );

  const onDelete = useCallback(
    (blockId: string) => {
      if (window.confirm('Delete this block? This is reversible (soft delete).')) {
        deleteBlock.mutate(blockId);
      }
    },
    [deleteBlock],
  );

  if (blocksQuery.isLoading) return <BlocksSkeleton />;
  if (blocksQuery.isError) return <BlocksError onRetry={() => blocksQuery.refetch()} />;

  return (
    <div className="space-y-4">
      {/* Conversion engine bar (instructor-only). Only shows when there's
          enough text in the lesson to convert from — the dropdown disables
          itself when the source is too short. */}
      {!readOnly && (
        <div className="flex items-center justify-end">
          <ConvertLessonMenu token={token} blocks={blocks} sourceModuleItemId={itemId} />
        </div>
      )}

      <AnimatePresence initial={false}>
        {blocks.map((block) => (
          <BlockRow
            key={block.id}
            block={block}
            readOnly={readOnly}
            onUpdate={(p) => onUpdate(block, p)}
            onDelete={() => onDelete(block.id)}
          />
        ))}
      </AnimatePresence>

      {blocks.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
          <p className="text-sm text-muted-foreground">
            This lesson is empty.{' '}
            {readOnly ? '' : 'Add your first block below — or let EduBorg draft a starter for you.'}
          </p>
          {/* Spark fill-in: instructor-only, only shown when there's no
              existing content (so we don't overwrite hand-authored blocks). */}
          {!readOnly && (
            <div className="mt-4 flex justify-center">
              <SparkLessonButton token={token} moduleItemId={itemId} />
            </div>
          )}
        </div>
      )}

      {!readOnly && <AddBlockBar onAdd={onAdd} pending={createBlock.isPending} />}
    </div>
  );
}

/* ─── Block row (one block + chrome) ──────────────────────────────────── */

function BlockRow({
  block,
  readOnly,
  onUpdate,
  onDelete,
}: {
  block: ContentBlockShape;
  readOnly: boolean;
  onUpdate: (payload: BlockPayload) => void;
  onDelete: () => void;
}) {
  const payload = parsePayload(block.type, block.payloadJson);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.2 }}
      className="group relative rounded-2xl border border-border bg-card p-4 shadow-sm"
    >
      {!readOnly && (
        <div className="absolute -left-9 top-4 hidden flex-col gap-1 opacity-0 transition-opacity group-hover:opacity-100 sm:flex">
          <button
            type="button"
            aria-label="Drag to reorder"
            className="cursor-grab rounded p-1 text-muted-foreground hover:bg-secondary"
          >
            <GripVertical className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Delete block"
            onClick={onDelete}
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      <BlockBody payload={payload} readOnly={readOnly} onUpdate={onUpdate} />
    </motion.div>
  );
}

/* ─── Per-type editor ─────────────────────────────────────────────────── */

function BlockBody({
  payload,
  readOnly,
  onUpdate,
}: {
  payload: BlockPayload;
  readOnly: boolean;
  onUpdate: (p: BlockPayload) => void;
}) {
  switch (payload.type) {
    case 'text':
      return (
        <TextArea
          value={payload.text}
          readOnly={readOnly}
          placeholder="Type a paragraph…"
          onCommit={(text) => onUpdate({ type: 'text', text })}
        />
      );
    case 'heading': {
      const level = payload.level;
      const Tag = (level === 1 ? 'h2' : level === 2 ? 'h3' : 'h4') as 'h2' | 'h3' | 'h4';
      const sizeClass = level === 1 ? 'text-2xl' : level === 2 ? 'text-xl' : 'text-lg';
      return readOnly ? (
        <Tag className={cn('font-display font-semibold', sizeClass)}>
          {payload.text || 'Heading'}
        </Tag>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Level</span>
            {[1, 2, 3].map((lv) => (
              <button
                key={lv}
                type="button"
                onClick={() => onUpdate({ ...payload, level: lv as 1 | 2 | 3 })}
                className={cn(
                  'rounded px-2 py-0.5 text-xs',
                  payload.level === lv
                    ? 'bg-accent text-accent-foreground'
                    : 'border border-border bg-card hover:bg-secondary',
                )}
              >
                H{lv}
              </button>
            ))}
          </div>
          <input
            value={payload.text}
            placeholder="Heading text"
            onChange={(e) => onUpdate({ ...payload, text: e.target.value })}
            className={cn(
              'block w-full bg-transparent font-display font-semibold outline-none',
              sizeClass,
            )}
          />
        </div>
      );
    }
    case 'document': {
      // Plain HTML editor (Phase 2 minimal). A rich editor (Tiptap or
      // ProseMirror) drops in here Phase 5 alongside the AI rewrite tools.
      return (
        <TextArea
          value={payload.html}
          readOnly={readOnly}
          placeholder="<p>Rich content goes here — HTML for now.</p>"
          monospace
          onCommit={(html) => onUpdate({ type: 'document', html })}
        />
      );
    }
    case 'embed':
      return readOnly ? (
        <p className="text-sm text-muted-foreground">
          Embed:{' '}
          <a href={payload.url} className="text-accent underline">
            {payload.url}
          </a>
        </p>
      ) : (
        <div className="space-y-2">
          <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Embed URL
          </label>
          <input
            type="url"
            value={payload.url}
            placeholder="https://www.youtube.com/embed/…"
            onChange={(e) => onUpdate({ ...payload, url: e.target.value })}
            className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
          />
        </div>
      );
    case 'image':
    case 'video':
    case 'file':
    case 'freehand':
      return <UploadStub kind={payload.type} />;
  }
}

/* ─── Stub for R2-dependent block types ─────────────────────────────── */

function UploadStub({ kind }: { kind: 'image' | 'video' | 'file' | 'freehand' }) {
  const labels: Record<typeof kind, { title: string; body: string }> = {
    image: {
      title: 'Image upload',
      body: 'Image uploads turn on once Cloudflare R2 is wired up (Phase 0 close-out).',
    },
    video: {
      title: 'Video upload',
      body: 'Self-hosted video uploads land with R2; external URLs (YouTube/Vimeo) work via the Embed block today.',
    },
    file: {
      title: 'File attachment',
      body: 'File attachments need R2. The block is here so you can plan around it.',
    },
    freehand: {
      title: 'Freehand whiteboard',
      body: 'The canvas + stroke serializer ships with R2 too — drawing surface is ready, persistence is not.',
    },
  };
  const m = labels[kind];
  return (
    <div className="flex items-start gap-3 rounded-xl border border-dashed border-border bg-secondary/40 p-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-card text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      </div>
      <div>
        <p className="text-sm font-semibold">{m.title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{m.body}</p>
      </div>
    </div>
  );
}

/* ─── TextArea with onCommit on blur ───────────────────────────────────── */

function TextArea({
  value,
  readOnly,
  placeholder,
  monospace,
  onCommit,
}: {
  value: string;
  readOnly: boolean;
  placeholder?: string;
  monospace?: boolean;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  if (readOnly) {
    return (
      <p className={cn('whitespace-pre-wrap text-sm leading-relaxed', monospace && 'font-mono')}>
        {value}
      </p>
    );
  }
  return (
    <textarea
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== value) onCommit(draft);
      }}
      placeholder={placeholder}
      className={cn(
        'block w-full resize-y rounded-lg border-0 bg-transparent p-1 text-sm leading-relaxed outline-none placeholder:text-muted-foreground/60',
        monospace && 'font-mono text-xs',
      )}
      rows={Math.max(3, Math.min(12, draft.split('\n').length + 1))}
    />
  );
}

/* ─── Add block bar ────────────────────────────────────────────────────── */

const ADD_OPTIONS: Array<{ type: ContentBlockType; label: string; icon: typeof Type }> = [
  { type: 'text', label: 'Text', icon: Type },
  { type: 'heading', label: 'Heading', icon: Heading2 },
  { type: 'document', label: 'Document', icon: FileText },
  { type: 'embed', label: 'Embed', icon: EmbedIcon },
  { type: 'image', label: 'Image', icon: ImageIcon },
  { type: 'video', label: 'Video', icon: VideoIcon },
  { type: 'file', label: 'File', icon: FileText },
  { type: 'freehand', label: 'Freehand', icon: PenTool },
];

function AddBlockBar({
  onAdd,
  pending,
}: {
  onAdd: (type: ContentBlockType) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/50 p-3">
      {open ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {ADD_OPTIONS.map((opt) => (
            <button
              key={opt.type}
              type="button"
              disabled={pending}
              onClick={() => {
                onAdd(opt.type);
                setOpen(false);
              }}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary disabled:opacity-60"
            >
              <opt.icon className="h-4 w-4 text-accent" aria-hidden />
              {opt.label}
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Plus className="h-4 w-4" aria-hidden /> Add block
        </button>
      )}
    </div>
  );
}

function BlocksSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-20 animate-pulse rounded-2xl border border-border bg-card/60" />
      ))}
    </div>
  );
}

function BlocksError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
      <p className="font-medium text-destructive">Couldn&apos;t load lesson content.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded-lg border border-border bg-card px-3 py-1 text-xs font-medium hover:bg-secondary"
      >
        Retry
      </button>
    </div>
  );
}
